import {StateCreator} from "zustand/vanilla";
import {companyPulseDigestsAPI, companyPulseFeedAPI, companyPulseStatusAPI, publicGlobalConfigAPI} from "@/app/apiPaths";
import {
  GetPulseDigestsRequest,
  GetPulseDigestsResponse,
  GetPulseEnrollmentResponse,
  GetPulseFeedResponse,
  UpdatePulseEnrollmentRequest,
  UpdatePulseEnrollmentResponse
} from "@/pb/api/devplan/services/web/company";
import {GetPublicGlobalConfigResponse} from "@/pb/api/devplan/services/web/user";
import {jr} from "@/hooks/queries/response-handler";
import {PulseDigest, PulseEnrollment, PulseFeedItem} from "@/pb/api/devplan/types/pulse";

export type PulseStoreState = {
  pulseEnrollment: Record<number, PulseEnrollment>,
  pulseDigests: Record<string, PulseDigest>,
  pulseFeedItems: PulseFeedItem[],
  pulseFeedHasCatalogue: boolean,
  fetchPublicConfig(): Promise<void>;
  fetchPulseEnrollment(companyId: number): Promise<void>;
  updatePulseEnrollment(enrollment: PulseEnrollment): Promise<void>;
  fetchPulseDigests(companyId: number, req: GetPulseDigestsRequest): Promise<string[]>;
  fetchPulseFeed(companyId: number): Promise<void>;
}

export const createPulseSlice: StateCreator<
  PulseStoreState,
  [],
  [],
  PulseStoreState
> = ((set) => ({
  pulseEnrollment: {},
  pulseDigests: {},
  pulseFeedItems: [],
  pulseFeedHasCatalogue: false,
  fetchPublicConfig: () => fetch(publicGlobalConfigAPI()).then(jr).then(js => {
    const {config: publicConfig} = GetPublicGlobalConfigResponse.fromJSON(js)
    set(s => ({...s, publicConfig}))
  }),
  fetchPulseEnrollment: async companyId => fetch(companyPulseStatusAPI(companyId))
    .then(jr)
    .then(js => {
      const {enrollment} = GetPulseEnrollmentResponse.fromJSON(js)
      set(s => ({...s, pulseEnrollment: {...s.pulseEnrollment, [companyId]: enrollment!}}))
    }),
  updatePulseEnrollment: async enrollment => fetch(companyPulseStatusAPI(enrollment.companyId), {
    method: "POST", body: JSON.stringify(UpdatePulseEnrollmentRequest.toJSON({enrollment}))
  }).then(jr)
    .then(js => {
      const {enrollment} = UpdatePulseEnrollmentResponse.fromJSON(js)
      set(s => ({...s, pulseEnrollment: {...s.pulseEnrollment, [enrollment!.companyId]: enrollment!}}))
    }),
  fetchPulseDigests: async (companyId, req) => fetch(companyPulseDigestsAPI(companyId), {
    method: "POST", body: JSON.stringify(GetPulseDigestsRequest.toJSON(req))
  })
    .then(jr)
    .then(js => {
      const {digests} = GetPulseDigestsResponse.fromJSON(js)
      const newDigests = digests.reduce((a, d) => ({...a, [d.contextifyId]: d}), {} as Record<string, PulseDigest>)
      set(s => ({...s, pulseDigests: {...s.pulseDigests, ...newDigests}}))
      return digests.map(d => d.contextifyId)
    }),
  fetchPulseFeed: async (companyId) => fetch(companyPulseFeedAPI(companyId))
    .then(jr)
    .then(js => {
      const {items, hasCatalogue} = GetPulseFeedResponse.fromJSON(js)
      set(s => ({...s, pulseFeedItems: items, pulseFeedHasCatalogue: hasCatalogue}))
    }),
}))
